<script setup lang="ts">
import { computed, ref, watch } from "vue";
import BaseButton from "@/shared/components/BaseButton.vue";
import type { VariableMeta } from "@/shared/services/variable-extractor";

const props = defineProps<{
  activeTab: string;
  editableFields: VariableMeta[];
  editableValues: Record<string, string>;
  allowEditing: boolean;
  /**
   * Campos que alguma assinatura do usuário realmente renderiza. `null` quando
   * ainda não se sabe (templates não carregados) — aí tudo é exibido, para o
   * formulário nunca aparecer vazio por falta de dado.
   */
  usedFields?: string[] | null;
}>();

const emit = defineEmits<{
  save: [overrides: Record<string, string>];
  logout: [];
}>();

const localEditable = ref<Record<string, string>>({});

watch(
  () => props.editableValues,
  (values) => {
    const next: Record<string, string> = { ...(values ?? {}) };
    for (const field of props.editableFields) {
      next[field.name] = values?.[field.name] ?? "";
    }
    localEditable.value = next;
  },
  { immediate: true, deep: true }
);

/**
 * Nomes dos campos autorais da aba Extras, que não têm origem no Microsoft 365.
 */
const extrasFields = computed(() => new Set(props.editableFields.map((field) => field.name)));

/**
 * `allow_editing` governa apenas os campos derivados (Contato e Endereço). Os
 * campos autorais da aba Extras não existem em nenhuma outra fonte, então ficam
 * sempre liberados
 */
function canEdit(name: string): boolean {
  return props.allowEditing || extrasFields.value.has(name);
}

const usedFieldSet = computed(() =>
  props.usedFields && props.usedFields.length > 0 ? new Set(props.usedFields) : null
);

/**
 * Só exibe o campo se alguma assinatura do usuário o utiliza.
 *
 * @param name - Nome do campo no template.
 * @returns `true` quando o campo deve aparecer no formulário.
 */
function isVisible(name: string): boolean {
  return !usedFieldSet.value || usedFieldSet.value.has(name);
}

function updateEditable(name: string, value: string): void {
  if (!canEdit(name)) return;
  localEditable.value = { ...localEditable.value, [name]: value };
}

function clearEditable(name: string): void {
  updateEditable(name, "");
}

function submitForm(): void {
  emit("save", { ...localEditable.value });
}
</script>

<template>
  <div class="profile-form">
    <div class="profile-form__body">
      <div v-show="activeTab === 'contact'" class="profile-form__panel">
        <div v-if="!allowEditing" class="profile-form__info" role="note">
          <v-icon name="md-lock-outlined" class="profile-form__info-icon" aria-hidden="true" />
          <p class="profile-form__info-text">
            Esses dados são sincronizados com o Microsoft 365. Caso precise alterá-los, entre em contato com o administrador.
          </p>
        </div>

        <div v-if="isVisible('display_name')" class="form-group">
          <label for="field_display_name" class="form-label">Nome</label>
          <div class="input-wrapper">
            <v-icon name="md-person-outlined" class="input-icon" aria-hidden="true" />
            <input
              id="field_display_name"
              v-bind:value="localEditable.display_name"
              type="text"
              v-bind:readonly="!allowEditing"
              v-bind:class="['form-input', { 'form-input--locked': !allowEditing }]"
              v-on:input="updateEditable('display_name', ($event.target as HTMLInputElement).value)"
            />
            <button
              v-if="allowEditing && localEditable.display_name"
              class="reset-btn"
              type="button"
              title="Voltar ao valor original"
              v-on:click="clearEditable('display_name')"
            >✕</button>
            <v-icon v-if="!allowEditing" name="md-lock-outlined" class="lock-icon" aria-hidden="true" />
          </div>
        </div>

        <div v-if="isVisible('email')" class="form-group">
          <label for="field_email" class="form-label">E-mail</label>
          <div class="input-wrapper">
            <v-icon name="md-email-outlined" class="input-icon" aria-hidden="true" />
            <input
              id="field_email"
              v-bind:value="localEditable.email"
              type="email"
              v-bind:readonly="!allowEditing"
              v-bind:class="['form-input', { 'form-input--locked': !allowEditing }]"
              v-on:input="updateEditable('email', ($event.target as HTMLInputElement).value)"
            />
            <button
              v-if="allowEditing && localEditable.email"
              class="reset-btn"
              type="button"
              title="Voltar ao valor original"
              v-on:click="clearEditable('email')"
            >✕</button>
            <v-icon v-if="!allowEditing" name="md-lock-outlined" class="lock-icon" aria-hidden="true" />
          </div>
        </div>

        <div v-if="isVisible('job_title')" class="form-group">
          <label for="field_job_title" class="form-label">Cargo</label>
          <div class="input-wrapper">
            <v-icon name="md-workoutline" class="input-icon" aria-hidden="true" />
            <input
              id="field_job_title"
              v-bind:value="localEditable.job_title"
              type="text"
              v-bind:readonly="!allowEditing"
              v-bind:class="['form-input', { 'form-input--locked': !allowEditing }]"
              v-on:input="updateEditable('job_title', ($event.target as HTMLInputElement).value)"
            />
            <button
              v-if="allowEditing && localEditable.job_title"
              class="reset-btn"
              type="button"
              title="Voltar ao valor original"
              v-on:click="clearEditable('job_title')"
            >✕</button>
            <v-icon v-if="!allowEditing" name="md-lock-outlined" class="lock-icon" aria-hidden="true" />
          </div>
        </div>

        <div v-if="isVisible('office_phone_1')" class="form-group">
          <label for="field_office_phone_1" class="form-label">Telefone 1</label>
          <div class="input-wrapper">
            <v-icon name="md-phone-outlined" class="input-icon" aria-hidden="true" />
            <input
              id="field_office_phone_1"
              v-bind:value="localEditable.office_phone_1"
              type="text"
              v-bind:readonly="!allowEditing"
              v-bind:class="['form-input', { 'form-input--locked': !allowEditing }]"
              v-on:input="updateEditable('office_phone_1', ($event.target as HTMLInputElement).value)"
            />
            <button
              v-if="allowEditing && localEditable.office_phone_1"
              class="reset-btn"
              type="button"
              title="Voltar ao valor original"
              v-on:click="clearEditable('office_phone_1')"
            >✕</button>
            <v-icon v-if="!allowEditing" name="md-lock-outlined" class="lock-icon" aria-hidden="true" />
          </div>
        </div>

        <div v-if="isVisible('office_phone_2')" class="form-group">
          <label for="field_office_phone_2" class="form-label">Telefone 2</label>
          <div class="input-wrapper">
            <v-icon name="md-phone-outlined" class="input-icon" aria-hidden="true" />
            <input
              id="field_office_phone_2"
              v-bind:value="localEditable.office_phone_2"
              type="text"
              v-bind:readonly="!allowEditing"
              v-bind:class="['form-input', { 'form-input--locked': !allowEditing }]"
              v-on:input="updateEditable('office_phone_2', ($event.target as HTMLInputElement).value)"
            />
            <button
              v-if="allowEditing && localEditable.office_phone_2"
              class="reset-btn"
              type="button"
              title="Voltar ao valor original"
              v-on:click="clearEditable('office_phone_2')"
            >✕</button>
            <v-icon v-if="!allowEditing" name="md-lock-outlined" class="lock-icon" aria-hidden="true" />
          </div>
        </div>
      </div>

      <div v-show="activeTab === 'address'" class="profile-form__panel">
        <div v-if="!allowEditing" class="profile-form__info" role="note">
          <v-icon name="md-lock-outlined" class="profile-form__info-icon" aria-hidden="true" />
          <p class="profile-form__info-text">
            Esses dados são sincronizados com o Microsoft 365. Caso precise alterá-los, entre em contato com o administrador.
          </p>
        </div>

        <div v-if="isVisible('street_address')" class="form-group">
          <label for="field_street_address" class="form-label">Logradouro</label>
          <div class="input-wrapper">
            <v-icon name="md-locationon-outlined" class="input-icon" aria-hidden="true" />
            <input
              id="field_street_address"
              v-bind:value="localEditable.street_address"
              type="text"
              v-bind:readonly="!allowEditing"
              v-bind:class="['form-input', { 'form-input--locked': !allowEditing }]"
              v-on:input="updateEditable('street_address', ($event.target as HTMLInputElement).value)"
            />
            <button
              v-if="allowEditing && localEditable.street_address"
              class="reset-btn"
              type="button"
              title="Voltar ao valor padrão"
              v-on:click="clearEditable('street_address')"
            >✕</button>
            <v-icon v-if="!allowEditing" name="md-lock-outlined" class="lock-icon" aria-hidden="true" />
          </div>
        </div>

        <div v-if="isVisible('city') || isVisible('state')" class="form-row">
          <div v-if="isVisible('city')" class="form-group form-group--flex">
            <label for="field_city" class="form-label">Cidade</label>
            <div class="input-wrapper">
              <v-icon name="md-locationon-outlined" class="input-icon" aria-hidden="true" />
              <input
                id="field_city"
                v-bind:value="localEditable.city"
                type="text"
                v-bind:readonly="!allowEditing"
                v-bind:class="['form-input', { 'form-input--locked': !allowEditing }]"
                v-on:input="updateEditable('city', ($event.target as HTMLInputElement).value)"
              />
              <v-icon v-if="!allowEditing" name="md-lock-outlined" class="lock-icon" aria-hidden="true" />
            </div>
          </div>

          <div v-if="isVisible('state')" class="form-group form-group--fixed">
            <label for="field_state" class="form-label">Estado</label>
            <div class="input-wrapper">
              <input
                id="field_state"
                v-bind:value="localEditable.state"
                type="text"
                v-bind:readonly="!allowEditing"
                v-bind:class="['form-input', 'form-input--bare', { 'form-input--locked': !allowEditing }]"
                v-on:input="updateEditable('state', ($event.target as HTMLInputElement).value)"
              />
              <v-icon v-if="!allowEditing" name="md-lock-outlined" class="lock-icon" aria-hidden="true" />
            </div>
          </div>
        </div>

        <div v-if="isVisible('zip_code') || isVisible('country')" class="form-row">
          <div v-if="isVisible('zip_code')" class="form-group form-group--flex">
            <label for="field_zip_code" class="form-label">CEP</label>
            <div class="input-wrapper">
              <input
                id="field_zip_code"
                v-bind:value="localEditable.zip_code"
                type="text"
                v-bind:readonly="!allowEditing"
                v-bind:class="['form-input', 'form-input--bare', { 'form-input--locked': !allowEditing }]"
                v-on:input="updateEditable('zip_code', ($event.target as HTMLInputElement).value)"
              />
              <v-icon v-if="!allowEditing" name="md-lock-outlined" class="lock-icon" aria-hidden="true" />
            </div>
          </div>

          <div v-if="isVisible('country')" class="form-group form-group--flex">
            <label for="field_country" class="form-label">País</label>
            <div class="input-wrapper">
              <input
                id="field_country"
                v-bind:value="localEditable.country"
                type="text"
                v-bind:readonly="!allowEditing"
                v-bind:class="['form-input', 'form-input--bare', { 'form-input--locked': !allowEditing }]"
                v-on:input="updateEditable('country', ($event.target as HTMLInputElement).value)"
              />
              <v-icon v-if="!allowEditing" name="md-lock-outlined" class="lock-icon" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>

      <div v-show="activeTab === 'extras'" class="profile-form__panel">
        <div class="profile-form__info" role="note">
          <v-icon name="md-info" class="profile-form__info-icon" aria-hidden="true" />
          <p class="profile-form__info-text">
            <template v-if="allowEditing">
              Campos exclusivos da sua assinatura, sem origem no Microsoft 365.
              Deixe em branco para omiti-los.
            </template>
            <template v-else>
              Estes são os únicos campos que você pode personalizar. Deixe em
              branco para omiti-los.
            </template>
          </p>
        </div>

        <div
          v-for="field in editableFields"
          v-bind:key="field.name"
          class="form-group"
        >
          <label v-bind:for="`editable_${field.name}`" class="form-label">{{ field.label }}</label>
          <div class="input-wrapper">
            <input
              v-bind:id="`editable_${field.name}`"
              v-bind:type="field.inputType"
              v-bind:placeholder="field.placeholder"
              v-bind:value="localEditable[field.name] || ''"
              class="form-input form-input--bare"
              v-on:input="updateEditable(field.name, ($event.target as HTMLInputElement).value)"
            />
            <button
              v-if="localEditable[field.name]"
              class="reset-btn"
              type="button"
              title="Limpar campo"
              v-on:click="clearEditable(field.name)"
            >✕</button>
          </div>
        </div>
      </div>
    </div>

    <div class="actions">
      <BaseButton size="lg" block v-on:click="submitForm">
        <v-icon name="md-save-outlined" /> Salvar dados
      </BaseButton>
      <BaseButton variant="ghost" block type="button" v-on:click="$emit('logout')">
        <v-icon name="md-logout" /> Sair da conta
      </BaseButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.profile-form {
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.profile-form__body {
  flex: 1;
  min-height: 368px;
  max-height: 60vh;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
}

.profile-form__panel {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.form-group { margin-bottom: var(--spacing-sm); }
.form-label {
  display: block;
  font-size: var(--font-size-sm);
  font-weight: 500;
  color: var(--colorText);
  margin-bottom: var(--spacing-xs);
}
.form-input {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-sm) var(--spacing-sm) 36px;
  font-size: var(--font-size-md);
  font-family: var(--font-family);
  color: var(--colorText);
  background-color: var(--colorBackgroundSubtle);
  border: 1px solid var(--colorBorder);
  border-radius: var(--radius-sm);
  transition: border-color var(--transition), box-shadow var(--transition), background-color var(--transition);
}
.form-input--bare {
  padding-left: var(--spacing-sm);
}
.form-input:focus {
  outline: none;
  border-color: var(--colorBorderFocus);
  box-shadow: 0 0 0 1px var(--colorBorderFocus);
  background-color: var(--colorBackground);
}
.form-input::placeholder { color: var(--colorTextSecondary); }

.form-input--locked {
  color: var(--colorTextSecondary);
  cursor: default;

  &:focus {
    border-color: var(--colorBorder);
    box-shadow: none;
    background-color: var(--colorBackgroundSubtle);
  }
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}
.input-wrapper .form-input {
  padding-right: 32px;
}
.input-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  color: var(--colorTextTertiary);
  pointer-events: none;
  flex-shrink: 0;
}
.form-input:not(.form-input--locked):focus ~ .input-icon,
.input-wrapper:focus-within .input-icon {
  color: var(--colorPrimary);
}
.lock-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  color: var(--colorTextTertiary);
  pointer-events: none;
}
.reset-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--colorTextSecondary);
  font-size: 14px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 50%;
  line-height: 1;
  transition: color var(--transition), background var(--transition);
}
.reset-btn:hover {
  color: var(--colorError);
  background: var(--colorBackgroundHover);
}

.profile-form__info {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  margin-bottom: var(--spacing-sm);
  border: 1px solid var(--colorBorderSubtle);
  border-left: 3px solid var(--colorInfo);
  border-radius: var(--radius-sm);
  background: var(--colorInfoBackground);
}

.profile-form__info-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  color: var(--colorInfo);
}

.profile-form__info-text {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: 1.45;
  color: var(--colorTextSecondary);
}

.form-row {
  display: flex;
  gap: var(--spacing-sm);
  align-items: flex-end;
}
.form-group--flex {
  flex: 1;
  min-width: 0;
}
.form-group--fixed {
  flex: 0 0 90px;
  width: 90px;
}

.actions {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-md);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--colorBorderSubtle);
  flex-shrink: 0;
  background: var(--colorBackground);
}

@media (max-width: 400px) {
  .profile-form { padding: var(--spacing-md); }
}
</style>
